import { all, call, put, takeEvery } from 'redux-saga/effects';
import { StatisticGroupingTypes } from 'modules/admin-panel/models';
import {
	convertCamelCaseIntoSnakeCase,
	convertSnakeCaseIntoCamelCase,
	requestSagaFactory,
} from 'helpers';
import { logSagaErrorMessage } from 'utils';
import { ENDPOINTS } from 'utils/constants';
import {
	GroupingType,
	IResponseClient,
	IResponseHeatmapStatistic,
	IResponseMetric,
	IResponseRound,
	IResponseServiceCards,
	IResponseServiceList,
	IResultFilterParams,
	IResultServiceParam,
	IStatisticItem,
	IStatisticPayloadWithType,
	ISummaryPayload,
	SurveyRoundsData,
} from '../models';
import {
	getAllSummaryData,
	getClientLogo,
	getSummaryMetrics,
	getSummaryHeatmapStatistic,
	getSummaryRounds,
	getSummaryServiceCards,
	getSummaryServiceList,
} from '../results.actions';
import * as types from '../ActionTypes';

const getRoundDataSaga = requestSagaFactory<ISummaryPayload, IResponseRound>({
	url: ENDPOINTS.resultDashboardRounds,
	actionApi: getSummaryRounds,
	logErrorMessage: 'Get rounds on the Summary page',
	parseData: (
		{ surveyList }: SurveyRoundsData,
		{ roundId }: ISummaryPayload,
	) => {
		const rounds = surveyList[0].surveyRoundList;
		return {
			rounds,
			activeRound: rounds.find((r) => r.surveyRoundId === roundId),
			surveyName: surveyList[0].name,
			isSurveyQuick: surveyList[0].quick,
		};
	},
});

const getServiceListSaga = requestSagaFactory<
	IResultServiceParam,
	IResponseServiceList
>({
	url: ENDPOINTS.resultDashboardServices,
	actionApi: getSummaryServiceList,
	logErrorMessage: 'Get services on the Summary page',
});

const getServiceCardsSaga = requestSagaFactory<
	IResultFilterParams,
	IResponseServiceCards
>({
	url: ENDPOINTS.resultDashboardServicesCards,
	actionApi: getSummaryServiceCards,
	logErrorMessage: 'Get services cards on the Summary page',
});

const getHeatmapStatisticSaga = requestSagaFactory<
	IStatisticPayloadWithType,
	IResponseHeatmapStatistic
>({
	url: ENDPOINTS.locationStatistic,
	actionApi: getSummaryHeatmapStatistic,
	logErrorMessage: 'Get summary heatmap statistic',
	parseData: (
		data: IStatisticItem[],
		{ groupingType }: IStatisticPayloadWithType,
	) => ({
		[`${convertSnakeCaseIntoCamelCase(groupingType)}Statistic`]: data,
	}),
});

const getClientLogoSaga = requestSagaFactory<
	{ surveyRoundId: string },
	IResponseClient
>({
	url: ENDPOINTS.clientLogo,
	actionApi: getClientLogo,
	logErrorMessage: 'Get client logo and name by survey round id',
});

const getReportMetricsSaga = requestSagaFactory<
	{ surveyRoundId: string },
	IResponseMetric
>({
	url: ENDPOINTS.reportMetrics,
	actionApi: getSummaryMetrics,
	logErrorMessage: 'Get report key metrics',
});

function* getAllSummaryDataSaga({
	payload: { surveyId, roundId },
}: {
	type: string;
	payload: ISummaryPayload;
}) {
	try {
		yield put(getAllSummaryData.loading());

		const allHeatmapStatisticCalls = Object.values(StatisticGroupingTypes).map(
			(type) => {
				const groupingType = convertCamelCaseIntoSnakeCase(
					type,
				) as GroupingType;
				return call(getHeatmapStatisticSaga, {
					payload: {
						surveyId,
						surveyRoundId: roundId,
						groupingType,
					},
				});
			},
		);

		yield all([
			call(getClientLogoSaga, { payload: { surveyRoundId: roundId } }),
			call(getReportMetricsSaga, { payload: { surveyRoundId: roundId } }),
			call(getRoundDataSaga, { payload: { surveyId, roundId } }),
			call(getServiceListSaga, {
				payload: { surveyRoundId: roundId },
			}),
			call(getServiceCardsSaga, {
				payload: { surveyId, surveyRoundId: roundId },
			}),
			...allHeatmapStatisticCalls,
		]);

		yield put(getAllSummaryData.success());
	} catch (e) {
		logSagaErrorMessage('Get comments data saga', e);
		yield put(getAllSummaryData.fail(e));
	}
}

export function* summarySaga() {
	yield all([
		takeEvery(types.GET_ALL_SUMMARY_DATA.DEFAULT, getAllSummaryDataSaga),
		takeEvery(types.GET_SUMMARY_SERVICE_CARDS.DEFAULT, getServiceCardsSaga),
	]);
}
